/**
 * Guarda de onde veio a visita para que a venda possa ser creditada à campanha
 * certa. O pixel sozinho não resolve isso: ele conta a conversão dentro do
 * Gerenciador de Anúncios, mas não deixa no nosso banco nenhum vínculo entre o
 * pedido e a campanha — que é o que o relatório do produtor precisa ler.
 */

const STORAGE_KEY = 'campaign_attribution';

/* Sete dias é a janela de clique padrão da Meta e do TikTok; trinta cobre o
   ciclo mais longo do Google Ads sem manter atribuição velha demais viva. */
const TTL_DAYS = 30;

const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const CLICK_IDS = ['fbclid', 'gclid', 'ttclid'];
const TRACKED_PARAMS = [...UTM_PARAMS, ...CLICK_IDS];

const MAX_FIELD_LENGTH = 255;
const MAX_URL_LENGTH = 2000;

function readStorage() {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        // Navegação anônima ou cookies bloqueados: seguir sem atribuição é
        // melhor do que derrubar o checkout.
        return null;
    }
}

function writeStorage(attribution) {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
    } catch {
    }
}

function isExpired(attribution) {
    const capturedAt = Date.parse(attribution?.captured_at ?? '');

    if (!Number.isFinite(capturedAt)) {
        return true;
    }

    return Date.now() - capturedAt > TTL_DAYS * 24 * 60 * 60 * 1000;
}

function trim(value, maxLength = MAX_FIELD_LENGTH) {
    if (typeof value !== 'string') {
        return null;
    }

    const clean = value.trim();

    return clean ? clean.slice(0, maxLength) : null;
}

function readCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));

    return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Lê os parâmetros de campanha da URL atual e, se houver algum, passa a ser a
 * atribuição vigente. Só sobrescreve quando há sinal novo: navegar para uma
 * página interna sem parâmetro nenhum não pode apagar o clique que trouxe a
 * pessoa.
 */
export function captureAttribution(search = window.location.search) {
    const params = new URLSearchParams(search);
    const captured = {};

    TRACKED_PARAMS.forEach(param => {
        const value = trim(params.get(param));

        if (value) {
            captured[param] = value;
        }
    });

    if (!Object.keys(captured).length) {
        return getAttribution();
    }

    const previous = getAttribution();

    const attribution = {
        ...captured,
        landing_url: trim(window.location.href, MAX_URL_LENGTH),
        referrer: trim(document.referrer, MAX_URL_LENGTH),
        captured_at: new Date().toISOString(),
        // O primeiro toque sobrevive às visitas seguintes: é ele que diz qual
        // campanha descobriu o comprador, mesmo que outra feche a venda.
        first_touch_at: previous?.first_touch_at || new Date().toISOString(),
    };

    writeStorage(attribution);

    return attribution;
}

export function getAttribution() {
    const attribution = readStorage();

    if (!attribution || isExpired(attribution)) {
        return null;
    }

    return attribution;
}

/**
 * Monta o que vai junto do pedido. Os cookies `_fbp`/`_fbc` são gravados pelo
 * próprio pixel da Meta no navegador e são o que dá à Conversions API condição
 * de casar o evento do servidor com a pessoa que clicou no anúncio — sem eles a
 * qualidade da correspondência despenca.
 */
export function getAttributionPayload() {
    const attribution = getAttribution();
    const fbp = readCookie('_fbp');
    const fbc = readCookie('_fbc') || buildFbcFrom(attribution);

    if (!attribution && !fbp && !fbc) {
        return null;
    }

    const payload = { ...(attribution || {}) };

    if (fbp) {
        payload.fbp = trim(fbp);
    }

    if (fbc) {
        payload.fbc = trim(fbc, 512);
    }

    return payload;
}

/**
 * Quando o comprador chega pelo anúncio mas o cookie `_fbc` ainda não foi
 * gravado (bloqueador, primeiro carregamento), a Meta aceita o valor montado a
 * partir do próprio `fbclid` no formato `fb.1.<timestamp>.<fbclid>`.
 */
function buildFbcFrom(attribution) {
    if (!attribution?.fbclid) {
        return null;
    }

    const capturedAt = Date.parse(attribution.captured_at);
    const timestamp = Number.isFinite(capturedAt) ? capturedAt : Date.now();

    return `fb.1.${timestamp}.${attribution.fbclid}`;
}

export function clearAttribution() {
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch {
    }
}
